import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-types-papi';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import PencilIcon from '@wix/da-shared-react/pkg/Icons/Pencil';
import TrashcanIcon from '@wix/da-shared-react/pkg/Icons/Trashcan';
import {
  BiEventType,
  BiUpdateType,
} from '@wix/da-shared-react/pkg/biLogger/constants';
import BiButton from '@wix/da-shared-react/pkg/biLogger/Components/BiButton';
import Button from '@wix/da-shared-react/pkg/Button';
import MediaSelector from '@wix/da-shared-react/pkg/MediaPopup/MediaSelector';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import TextTooltipOnHover from '@wix/da-shared-react/pkg/Tooltip/TextTooltipOnHover';
import {
  UpdateCoverDeviationPayload,
  UpdateCoverDeviationOffsetYPayload,
} from '../../../../actions/deviations';
import { ModuleType } from '../../../../../types/modules';
import HeroEditPrimaryButton from './HeroEditPrimaryButton';
import HeroEditSecondaryButton from './HeroEditSecondaryButton';

import s from './ProfileHeroEditControls.scss';

export interface Props {
  coverDeviation?: PapiDeviation;
  isRepositioning: boolean;
  setIsRepositioning: (payload: boolean) => void;
  setSelectedDeviation: (payload: PapiDeviation) => void;
  updateCoverDeviation: (payload: UpdateCoverDeviationPayload) => void;
  updateCoverDeviationOffsetY: (
    payload: UpdateCoverDeviationOffsetYPayload
  ) => void;
  deleteCoverDeviation: () => void;
  openIntroPopup: () => void;
  dropProfileEditAidFlag?: () => void;
  className: string;
  showProfileHeroAid?: boolean;
  showProfileEditAid?: boolean;
  showWatchIntro?: boolean;
}

export interface State {
  showMediaManager: boolean;
}

export const ProfileHeroEditControls: React.FC<Props> = props => {
  const {
    showProfileHeroAid,
    coverDeviation,
    isRepositioning,
    showWatchIntro,
    className,
    setSelectedDeviation,
    setIsRepositioning,
  } = props;
  const { t } = useTranslation();
  const [showMediaManager, setShowMediaManager] = useState(false);

  const renderButtons = () => {
    if (isRepositioning) {
      return renderButtonsWhenRepositioning(props, { t });
    }
    if (coverDeviation) {
      return (
        <>
          {renderDeleteImageButton(props, { t })}
          {renderSetImageButton(props, { t, setShowMediaManager })}
        </>
      );
    }
    return (
      <TextTooltipOnHover
        tooltipText={t('common.profileHero.addCoverImage.tooltip')}
        className={s['icon-btn-wrapper']}
        contentClassName={s['ftue-tooltip-content']}
      >
        {renderSetImageButton(props, { t, setShowMediaManager })}
        {showProfileHeroAid && <div className={s['glow']} />}
      </TextTooltipOnHover>
    );
  };

  return (
    <div
      className={classnames(
        s['root-wrapper'],
        showProfileHeroAid && s['is-ftue'],
        className
      )}
    >
      {isRepositioning && renderDraggableImageHint({ t })}
      <div className={classnames(s['buttons'])}>
        {showWatchIntro && renderWatchIntroButton(props, { t })}
        {renderButtons()}
        {showMediaManager && (
          <MediaSelector
            mediaSourceTypes={[MediaSourceType.GALLERY, MediaSourceType.STASH]}
            allowedDeviationTypes={[DeviationTypes.IMAGE]}
            allowGifs={false}
            onClose={() => setShowMediaManager(false)}
            onSelect={selectedDeviations => {
              setSelectedDeviation(selectedDeviations[0]);
              setIsRepositioning(true);
              setShowMediaManager(false);
            }}
          />
        )}
      </div>
    </div>
  );
};
ProfileHeroEditControls.displayName = 'ProfileHeroEditControls';

const renderDraggableImageHint = ({ t }) => (
  <div className={s['draggable-hint']}>
    <div className={s['triple-stripe']}>
      <div className={s['stripe']} />
      <div className={s['stripe']} />
      <div className={s['stripe']} />
    </div>
    {t('common.profileHero.dragToMove')}
  </div>
);

const renderWatchIntroButton = (
  { openIntroPopup, dropProfileEditAidFlag }: Props,
  { t }
) => (
  <TextTooltipOnHover
    className={classnames(s['icon-btn-wrapper'], s['watch-intro-control'])}
    tooltipText={t('common.profileHero.watchIntro.tooltip')}
    renderReference={({ ref }) => (
      <Button
        view="b34"
        height="medium"
        data-role="edit-control"
        onClick={() => {
          openIntroPopup && openIntroPopup();
          dropProfileEditAidFlag && dropProfileEditAidFlag();
        }}
      >
        <span ref={ref}>{t('common.profileHero.empty.watchIntro')}</span>
      </Button>
    )}
  />
);

const renderSetImageButton = (
  { showProfileHeroAid, coverDeviation }: Props,
  { t, setShowMediaManager }
) => {
  return (
    <Button
      key="change-btn"
      className={s['icon-btn']}
      onClick={() => setShowMediaManager(true)}
      data-role={showProfileHeroAid ? 'ftue-control' : 'edit-control'}
      view="b8"
      biData={{
        evid: BiEventType.PROFILE_COVER_CLICK,
        is_update: BiUpdateType.CHANGE,
      }}
      aria-label={t('common.profileHero.editCoverImage')}
    >
      <span className={s['icon-btn-content']}>
        <PencilIcon className={s['icon']} size={IconSize.MEDIUM} condensed />
        <span className={s['btn-title']}>
          {t(
            coverDeviation
              ? 'common.profileHero.editCoverImage'
              : 'common.profileHero.addCoverImage'
          )}
        </span>
      </span>
    </Button>
  );
};

const renderButtonsWhenRepositioning = (
  { setIsRepositioning }: Props,
  { t }
) => (
  <>
    <HeroEditSecondaryButton
      biData={{
        evid: BiEventType.DEFAULT,
        click_info: 'profile.hero.repositioning.cancel',
      }}
      onClick={() => setIsRepositioning(false)}
    >
      {t('common.cancel')}
    </HeroEditSecondaryButton>
    <HeroEditPrimaryButton
      biData={{
        evid: BiEventType.DEFAULT,
        click_info: 'profile.hero.repositioning.done',
      }}
    >
      {t('common.done')}
    </HeroEditPrimaryButton>
  </>
);

const renderDeleteImageButton = (
  { deleteCoverDeviation, showProfileHeroAid }: Props,
  { t }
) => (
  <span className={s['delete-btn-wrapper']}>
    <TextTooltipOnHover
      tooltipText={t('common.profileHero.delete')}
      tooltipOffset={'0, 4'}
      renderReference={({ ref }) => (
        <BiButton
          key="delete-btn"
          className={classnames(s['icon-btn'], s['delete-btn'])}
          onClick={deleteCoverDeviation}
          data-role={showProfileHeroAid ? 'ftue-control' : 'edit-control'}
          biData={{
            evid: BiEventType.REMOVE_WIDGET_CLICK,
            widgetname: ModuleType.COVER_DEVIATION,
          }}
          aria-label={t('common.profileHero.a11y.removeCoverImage')}
        >
          <TrashcanIcon
            className={s['icon']}
            size={IconSize.MEDIUM}
            containerRef={ref}
            condensed
          />
        </BiButton>
      )}
    />
  </span>
);

export default ProfileHeroEditControls;
